import fetch from 'isomorphic-unfetch';
// @ts-ignore
import qs from 'query-string';
import { merge, jsonToCamel, filterJson } from 'ava-util/dist/data';
import { getStoreState } from 'base/store';
import { getCookie, delCookie } from 'ava-util/dist/cookie';
import { isJson } from 'ava-util/dist/data';
import { COOKIE_ACCESS_TOKEN, COOKIE_LOGIN_TOKEN } from 'base/config/constant';
import router from 'next/router';
import { RouteEnum } from 'pro/config/routers';
import { generatePath } from 'ava-util/dist/request';
import { getCurrentTimeZoneIANA } from 'base/utils/time-zone';
import { addAppLogGroup } from '@base/common/AppDebug';

interface ApiOptions {
    baseUrl?: string;
    params?: {};
    data?: {};
    fetchOptions?: FetchOptions
    debug?: boolean;
    commonErrorHandler?: boolean;
    isForm?: boolean;
    isUpload?: boolean;
    forceCamel?: boolean;
}

interface FetchOptions {
    method?: 'get' | 'post';
    headers?: {
        Accept?: 'application/json';
        'Content-Type'?: string;
    };
    credentials?: 'include';
    timeout?: 5000;
    mode?: 'same-origin';
    cache?: 'no-cache';
    body?: any;
}

function isBrowser(): boolean {
    return (process as any).browser;
}

/**
 * 获取包含协议和域名的完整域名
 * @param url
 * @param baseUrl
 */
function getFullUrl(url: string, baseUrl: string = ''): string {
    // http(s):// 开头
    if (/^(http:|https:)+\/\//.test(url)) {
        return url;
    }
    // //[域名] 开头
    if (/^\/\//.test(url)) {
        return (isBrowser() ? window.location.protocol : (url.indexOf('localhost') === -1 && process.env.NODE_ENV === 'production') ? 'https:' : 'http:') + url;
    }

    const { host } = getStoreState('rootStore');

    // 去掉 url 开头的斜杠
    url = url.replace(/^\/+/, '');
    baseUrl = baseUrl || `//${host}`;

    // 去掉 baseUrl 末尾的斜杠
    baseUrl = baseUrl.replace(/\/+$/, '');

    return getFullUrl(`${baseUrl}/${url}`); // 重新执行一次用来检查 protocol
}

export const api = Object.create(null);

// browser support methods ['DELETE', 'GET', 'HEAD', 'OPTIONS', 'POST', 'PATCH', 'PUT']
const METHODS = ['get', 'post'];
const ERROR_CODES = {
    loginTokenExpired: 100001,
    loginTokenNotMatch: 100003, // 当前登录信息不匹配请求参数
};

METHODS.forEach(method => {
    api[method] = (url: string, apiOpts: ApiOptions = {}): Promise<{}> => {
        // 过滤空数据
        apiOpts = <ApiOptions>filterJson(apiOpts);

        // 处理url开头的斜杠
        let fullUrl = getFullUrl(url, apiOpts.baseUrl);
        const { lang, country, currency, loginToken, accessToken, ua } = getStoreState('rootStore');

        const headers: Record<string, string> = {
            Accept: 'application/json',
            Lang: lang,
            Country: country || 'US',
            Currency: currency || 'USD',
            'X-Timezone': isBrowser() ? getCurrentTimeZoneIANA() : '',
            'LOGIN-TOKEN': (isBrowser() ? getCookie(COOKIE_LOGIN_TOKEN) : loginToken) || '',
            'ACCESS-TOKEN': (isBrowser() ? (getCookie(COOKIE_ACCESS_TOKEN) || accessToken) : accessToken) || '',
            'SERVER-PLATFORM': ua.isPhone ? 'm' : 'pc',
        };
        if (!isBrowser()) {
            headers['X-Skip-Rate-Limit-ldbd'] = '1';
        }
        // 模拟 wholesale domain
        if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_FROM_DOMAIN) {
            headers['from-domain'] = process.env.NEXT_PUBLIC_FROM_DOMAIN;
        }

        const fetchOpts = <FetchOptions>merge({
            // @ts-ignore
            method,
            headers,
            credentials: 'include',
            cache: 'no-cache'
        }, apiOpts.fetchOptions || {});

        // 处理 query 和 body
        if (apiOpts.params) {
            // apiOpts.params 组合到url中
            fullUrl += `${fullUrl.includes('?') ? '&' : '?'}${qs.stringify(apiOpts.params, { arrayFormat: 'bracket' })}`;
        }

        // get 以外的请求将 apiOpts.data 赋值给body
        if (apiOpts.data && method === 'post') {
            if (apiOpts.isUpload) {
                if (isJson(apiOpts.data)) {
                    const formData = new FormData();
                    Object.entries(apiOpts.data).forEach(([name, value]) => {
                        formData.append(name, value as string | Blob);
                    })
                    fetchOpts.body = formData;

                } else {
                    fetchOpts.body = apiOpts.data;
                }

            } else {
                if (apiOpts.isForm) {
                    (fetchOpts.headers as Record<string, any>)['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
                } else {
                    (fetchOpts.headers as Record<string, any>)['Content-Type'] = 'application/json';
                }
                fetchOpts.body = JSON.stringify(apiOpts.data);
            }
        } else {
            (fetchOpts.headers as Record<string, any>)['Content-Type'] = 'application/json';
        }

        if (apiOpts.debug) {
            console.log('----- api debug -----');
            console.log('url: ', fullUrl);
            console.log('fetchOpts: ', fetchOpts);
            return new Promise((resolve) => resolve({}));
        }

        return fetch(fullUrl, fetchOpts as RequestInit).then(res => {
            if (res.status >= 400) {
                throw new Error(`[${res.status}] ${res.statusText}`);
            }
            return res.json();
        }).then(res => {
            // code 为 0 时返回data，不为 0 时抛出错误
            if (res.code === 0) {
                addAppLogGroup(fullUrl, {
                    fullUrl,
                    fetchOpts,
                    response: res
                });
                return apiOpts.forceCamel === false ? res.data : jsonToCamel(res.data);
            } else {
                throw res;
            }
        }).catch(err => {
            if (typeof window !== 'undefined') {
                addAppLogGroup(fullUrl, {
                    fullUrl,
                    fetchOpts,
                    error: err
                });

                if (err.code === ERROR_CODES.loginTokenExpired) {
                    delCookie(COOKIE_LOGIN_TOKEN);
                    if (!apiOpts.commonErrorHandler) {
                        router.push(generatePath(RouteEnum.LOGIN, { redirect: router.asPath || RouteEnum.SHOP }));
                    }
                } else if (err.code === ERROR_CODES.loginTokenNotMatch) {
                    router.push(generatePath(RouteEnum.ORDERS));
                }
            }

            const request = {
                url: fullUrl,
                ...fetchOpts
            };
            if (typeof window === 'undefined') {
                err.request = JSON.stringify(request);
                const rejectError = JSON.parse(JSON.stringify(err, Object.getOwnPropertyNames(err)));
                console.log(rejectError); // 服务端打印报错信息
                return Promise.reject(rejectError);
            } else {
                err.request = request;
                return Promise.reject(err);
            }

        });
    };
});

export default api;
